import { useState } from "react";
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Archive, Edit3, Package, Send, Undo2 } from "lucide-react";
import { Button, Card, CardBody, CardHeader, MetaPill, StatusPill, Skeleton, ConfirmDialog, toast } from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { BackButton } from "../../workspace/index.ts";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { perPiecePaise, priceChainPaise } from "../price-math.ts";
import { paiseToInrExact } from "../../../lib/format.ts";
import { compositionPairs, entryPiecesOnHand } from "./size-ratio.ts";
import {
  variantKey,
  variantQtyFromBundles,
} from "@flint/domain/commerce/store/variant-matrix";
import { StockBadge } from "./stock-badge.tsx";
import { StorePageColumn } from "../store-page-column.tsx";
import { STATUS_TONE } from "./listing-status.ts";
import { useListingVisibility } from "./use-listing-visibility.ts";
import { QrCodeCard } from "./qr-code-card.tsx";


function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-line py-2 last:border-0">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-right text-sm text-ink">{value}</span>
    </div>
  );
}

/**
 * Store admin — Product detail (view). Commerce V1.0.1 M2.2 — on @flint/ui + tokens.
 */
export function ProductDetailPage({ listingId }: { listingId: string }) {
  const q = trpc.storeCatalog.get.useQuery({ listingId });
  // Orders card is permission-gated like the orders module itself — a
  // catalog-only member sees no order data, not an error.
  const canViewOrders = usePermission("store.orders.view");
  const recentOrders = trpc.storeOrders.listForListing.useQuery(
    { listingId, limit: 5 },
    { enabled: canViewOrders },
  );
  const [archiveOpen, setArchiveOpen] = useState(false);
  // Minting is a write (spec 52 R9) — without the permission the QR card
  // explains instead of offering a dead button.
  const canManageCatalog = usePermission("store.catalog.manage");
  const vis = useListingVisibility();
  const p = q.data;
  /** Variant stock, derived from this listing's own bundles (ADR-0061) —
   *  the same helper the product form's table uses, over the same figures. */
  const variantQty = variantQtyFromBundles(p?.pricingEntries ?? []);

  return (
    <StoreAdminLayout
      breadcrumbs={[
        { label: "Store", to: "/store/admin" },
        { label: "Products", to: "/store/admin/products" },
        { label: p?.name ?? "…" },
      ]}
    >
      {!p ? (
        <StorePageColumn>
          <BackButton fallbackTo="/store/admin/products" label="Back to products" />
          <div className="mt-4">
            {q.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <p className="text-sm text-muted">Product not found.</p>
            )}
          </div>
        </StorePageColumn>
      ) : (
        <StorePageColumn>
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line pb-5">
            <div className="flex items-start gap-4">
              <BackButton
                fallbackTo="/store/admin/products"
                label="Back to products"
                className="mt-1"
              />
              {p.images[0] ? (
                <img
                  src={p.images[0]}
                  alt=""
                  className="h-14 w-14 shrink-0 rounded border border-line object-cover"
                />
              ) : (
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded border border-line bg-raised text-muted">
                  <Package className="h-6 w-6" aria-hidden />
                </span>
              )}
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">{p.name}</h1>
                <div className="mt-1 flex items-center gap-2 text-sm text-muted">
                  <span>{p.sku ?? "—"}</span>
                  <StatusPill tone={STATUS_TONE[p.visibility]} size="sm" dot>
                    {p.visibility}
                  </StatusPill>
                  {/* The same badge the products list draws, from the same
                      component, so a product reads identically wherever it is
                      opened (spec 13 R4c). */}
                  <StockBadge entries={p.pricingEntries} visibility={p.visibility} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button asChild intent="primary" size="md">
                <Link to="/store/admin/products/$listingId/edit" params={{ listingId }}>
                  <Edit3 size={14} aria-hidden />
                  Edit
                </Link>
              </Button>
              {/* Publish doubles as un-archive (draft + archived); Unpublish
                  covers published. Mutually exclusive. @see ADR-0050 */}
              {p.visibility !== "published" ? (
                <Button
                  intent="secondary"
                  size="md"
                  disabled={vis.isPending}
                  onClick={() => {
                    // Publishing needs ≥1 pack price (R5/AC9). The server floor
                    // is `publishListing` (throws ListingNotPublishableError);
                    // this pre-check is defense-in-depth so the operator gets an
                    // inline toast instead of a round-trip error.
                    if (p.pricingEntries.length === 0) {
                      toast.error("Add at least one pack price", {
                        description: "A product needs a pack price before it can be published.",
                      });
                      return;
                    }
                    void vis.setVisibility(listingId, "published").catch(() => {});
                  }}
                >
                  <Send size={14} aria-hidden />
                  Publish
                </Button>
              ) : null}
              {p.visibility === "published" ? (
                <Button
                  intent="secondary"
                  size="md"
                  disabled={vis.isPending}
                  onClick={() => void vis.setVisibility(listingId, "draft").catch(() => {})}
                >
                  <Undo2 size={14} aria-hidden />
                  Unpublish
                </Button>
              ) : null}
              {p.visibility !== "archived" ? (
                <Button intent="secondary" size="md" onClick={() => setArchiveOpen(true)}>
                  <Archive size={14} aria-hidden />
                  Archive
                </Button>
              ) : null}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Card>
              <CardHeader><h2 className="text-sm font-semibold text-ink">Details</h2></CardHeader>
              <CardBody>
                <Row label="SKU" value={p.sku ?? "—"} />
                <Row label="HSN" value={p.hsn ?? "—"} />
                <Row label="Category" value={p.category ?? "—"} />
                {/* Always shown, even as "—": a blank row says the product is
                    filed one level deep, which a hidden row would leave the
                    reader guessing at. */}
                <Row label="Sub-category" value={p.subCategory ?? "—"} />
                <Row label="Brand" value={p.brand ?? "—"} />
                <Row label="Country of origin" value={p.countryOfOrigin ?? "—"} />
                <Row label="Unit" value={p.unit} />
                {/* Pack-only pricing (spec 13, R4): a product is always sold in
                    packs — the summary names it plainly rather than deriving it. */}
                <Row label="Sold as" value="Pack" />
              </CardBody>
            </Card>

            {/* One block per pack, not one price for the product. Squeezed into
                a right-aligned `Row` value, two entries were unreadable — and a
                product priced in three packs is the ordinary case now. */}
            <Card>
              <CardHeader>
                <h2 className="text-sm font-semibold text-ink">
                  Bundles
                  {p.pricingEntries.length > 1 ? (
                    <span className="ml-2 font-normal text-muted">
                      {p.pricingEntries.length} bundles
                    </span>
                  ) : null}
                </h2>
              </CardHeader>
              <CardBody>
                <Row label="GST" value={p.taxRateBps === null ? "—" : `${p.taxRateBps / 100}%`} />
                {p.pricingEntries.length === 0 ? (
                  // Draft with no packs priced yet — publishing needs one (R5).
                  <div className="rounded-sm border border-dashed border-line px-4 py-6 text-center">
                    <p className="text-sm text-ink-2">No bundles yet</p>
                    <p className="mt-1 text-xs text-muted">Publishing needs at least one.</p>
                    <Button asChild intent="primary" size="sm" className="mt-3">
                      <Link to="/store/admin/products/$listingId/edit" params={{ listingId }}>
                        Add bundle
                      </Link>
                    </Button>
                  </div>
                ) : (
                  <ul className="flex flex-col gap-2 pt-2">
                    {p.pricingEntries.map((e, i) => {
                      // was → discount → final, all incl-GST, from the one helper the
                      // grid and the entry card also use (spec 13 R6a).
                      const { wasPaise, finalPaise, savingPaise } = priceChainPaise(
                        BigInt(e.pricePaise || "0"),
                        e.discountBps ?? 0,
                        p.taxRateBps,
                      );
                      // Per-piece is a display-only derivation of the pack price
                      // (R6/AC8) — never stored, never used in arithmetic. Derived from
                      // the incl-GST final so it sits on the same basis as the figure
                      // directly above it.
                      const perPiece = e.pieces > 0 ? perPiecePaise(finalPaise, e.pieces) : null;
                      return (
                        <li
                          key={`${e.label}-${i}`}
                          className="flex flex-wrap items-start justify-between gap-3 rounded-sm border border-line px-3 py-2.5"
                        >
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-baseline gap-2">
                              <span className="text-sm font-medium text-ink">{e.label}</span>
                              {i === 0 ? (
                                <span className="rounded-full bg-raised px-2 py-0.5 text-xs text-muted">
                                  primary
                                </span>
                              ) : null}
                              <span className="font-mono text-xs text-muted tabular-nums">
                                {e.pieces} pcs
                              </span>
                              {/* The bundle's colour overlay (#235). */}
                              {e.colorSpec ? (
                                <span className="text-xs text-muted">
                                  ·{" "}
                                  {e.colorSpec.mode === "single"
                                    ? e.colorSpec.colors[0]
                                    : `Mixed · ${e.colorSpec.colors.length} colors`}
                                </span>
                              ) : null}
                              {/* Same line as the form's entry card, so the two
                                  screens read identically. Absent quantity
                                  renders nothing rather than a placeholder.

                                  Tested against `undefined`, not truthiness:
                                  fulfilment can deduct an entry to 0 (ADR-0056),
                                  and a falsy test would hide exactly the
                                  sold-out case — making "none left" look
                                  identical to "never tracked". */}
                              {e.packsOnHand !== undefined ? (
                                <span
                                  className={
                                    e.packsOnHand === 0 ? "text-xs text-bad" : "text-xs text-muted"
                                  }
                                >
                                  ·{" "}
                                  <span className="font-mono tabular-nums">{e.packsOnHand}</span>{" "}
                                  packs ·{" "}
                                  <span className="font-mono tabular-nums">
                                    {entryPiecesOnHand({
                                      packsOnHand: e.packsOnHand,
                                      pieces: e.pieces,
                                    }).toLocaleString("en-IN")}
                                  </span>{" "}
                                  pcs
                                </span>
                              ) : null}
                            </div>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {compositionPairs(e.sizeRatio).map((c) => (
                                <MetaPill key={c.label}>
                                  {c.label}×{c.qty}
                                </MetaPill>
                              ))}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-muted">
                              {/* Struck through only when there is a discount — with
                                  none it is the same figure as the line below, not a
                                  superseded one. */}
                              <span
                                className={`font-mono tabular-nums${
                                  e.discountBps ? " line-through" : ""
                                }`}
                              >
                                {paiseToInrExact(wasPaise.toString())}
                              </span>
                              {e.discountBps ? (
                                <>
                                  {" "}
                                  {/* Numerals stay mono/tabular per brand-spec §3.4,
                                      even inside a pill. */}
                                  <MetaPill>
                                    <span className="font-mono tabular-nums">
                                      −{e.discountBps / 100}%
                                    </span>{" "}
                                    ·{" "}
                                    <span className="font-mono tabular-nums">
                                      −{paiseToInrExact(savingPaise.toString())}
                                    </span>
                                  </MetaPill>
                                </>
                              ) : null}
                            </div>
                            <div className="mt-0.5 font-mono font-semibold tabular-nums text-ink">
                              {paiseToInrExact(finalPaise.toString())}
                            </div>
                            <div className="text-xs text-muted">
                              MOQ {e.moq ?? "—"}{e.moq ? " packs" : ""}
                            </div>
                            {perPiece !== null ? (
                              <div className="text-xs text-muted" aria-hidden>
                                ≈{" "}
                                <span className="font-mono tabular-nums">
                                  {paiseToInrExact(perPiece.toString())}
                                </span>
                                /pc
                              </div>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardBody>
            </Card>

            {/* Inventory — the piece ledger per warehouse (UX finding 4 display
                slice). Follows store sales since ADR-0065. */}
            <Card>
              <CardHeader>
                <h2 className="text-sm font-semibold text-ink">
                  Inventory
                  {p.warehouseStock.length > 0 ? (
                    <span className="ml-2 font-mono text-sm font-normal tabular-nums text-muted">
                      {p.stockPiecesTotal.toLocaleString("en-IN")} pcs
                    </span>
                  ) : null}
                </h2>
              </CardHeader>
              <CardBody>
                {p.warehouseStock.length === 0 ? (
                  <p className="text-sm text-muted">
                    No warehouse stock recorded — post opening stock on creation, or a GRN /
                    adjustment in Inventory.
                  </p>
                ) : (
                  <>
                    {p.warehouseStock.map((w) => (
                      <Row
                        key={w.warehouseId}
                        label={w.warehouseName}
                        value={
                          <span className="font-mono tabular-nums">
                            {w.pieces.toLocaleString("en-IN")} pcs
                          </span>
                        }
                      />
                    ))}
                    <p className="mt-2 text-xs text-muted">
                      Available pieces in the stock ledger. Store fulfilments deduct from the
                      default warehouse; movements live in Inventory → Stock.
                    </p>
                  </>
                )}
              </CardBody>
            </Card>

            {/* The product's QR permalink, directly on the page (spec 52 R9):
                the code once minted, an inline Generate button before. */}
            <QrCodeCard
              listingId={listingId}
              qrUrl={p.qrUrl}
              fileStem={p.sku ?? "product"}
              visibility={p.visibility}
              canManage={canManageCatalog}
            />

            {/* Recent orders carrying this product — drafts included. Gated on
                the orders permission, like the module it links into. */}
            {canViewOrders ? (
              <Card>
                <CardHeader>
                  <h2 className="text-sm font-semibold text-ink">Orders</h2>
                </CardHeader>
                <CardBody>
                  {(recentOrders.data?.items.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted">
                      {recentOrders.isLoading ? "Loading orders…" : "No orders for this product yet."}
                    </p>
                  ) : (
                    <ul className="flex flex-col">
                      {recentOrders.data!.items.map((o) => (
                        <li
                          key={o.orderId}
                          className="flex items-baseline justify-between gap-3 border-b border-line py-1.5 text-sm last:border-b-0"
                        >
                          <Link
                            to="/store/admin/orders/$orderId"
                            params={{ orderId: o.orderId }}
                            className="font-medium text-accent hover:underline"
                          >
                            {o.docNumber ?? "Draft"}
                          </Link>
                          <span className="text-xs capitalize text-muted">{o.status}</span>
                          <span className="font-mono tabular-nums text-ink-2">
                            {o.packs} {o.packs === 1 ? "pack" : "packs"}
                          </span>
                          <span className="text-xs text-muted">
                            {new Date(o.createdAt).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "short",
                            })}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            ) : null}

            {p.images.length > 0 ? (
              <Card className="lg:col-span-2">
                <CardHeader><h2 className="text-sm font-semibold text-ink">Images</h2></CardHeader>
                <CardBody>
                  <div className="flex flex-wrap gap-3">
                    {p.images.map((src, i) => (
                      <div key={`${src}-${i}`} className="relative h-24 w-24 overflow-hidden rounded-sm border border-line bg-raised">
                        <img src={src} alt="" className="h-full w-full object-cover" />
                        {i === 0 ? (
                          <span className="absolute left-1 top-1 rounded-full border border-line bg-paper px-1.5 py-0.5 text-xs font-medium text-sky-700">
                            Primary
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            ) : null}

            <Card className="lg:col-span-2">
              <CardHeader><h2 className="text-sm font-semibold text-ink">Merchandising</h2></CardHeader>
              <CardBody>
                <Row label="Short description" value={p.shortDesc ?? "—"} />
                {p.longDesc ? (
                  <div className="border-b border-line py-2 last:border-0">
                    <div className="text-sm text-muted">Long description</div>
                    <p className="mt-1 text-sm text-ink">{p.longDesc}</p>
                  </div>
                ) : null}
                {Object.keys(p.attributes).length > 0 ? (
                  <div className="flex flex-wrap gap-2 pt-3">
                    {Object.entries(p.attributes).map(([k, v]) => (
                      <StatusPill key={k} tone="muted" size="sm">
                        {k}: {v}
                      </StatusPill>
                    ))}
                  </div>
                ) : (
                  // Names the fields rather than saying "attributes": since the
                  // variant matrix arrived, a product can visibly carry Size /
                  // Color attributes in the card below while this line claims
                  // none are set — the generic word now reads as a contradiction.
                  <p className="pt-3 text-sm text-muted">
                    No material, GSM, colour or care set.
                  </p>
                )}
              </CardBody>
            </Card>

            {/* Shown when EITHER half of the matrix exists: axes without rows
                is a real state (matrix cleared, definitions kept), and hiding
                the axes was exactly the gap the operator called out. */}
            {p.variants.length > 0 || p.variantAttributes.length > 0 ? (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <h2 className="text-sm font-semibold text-ink">
                    Variants{" "}
                    <span className="font-mono tabular-nums text-muted">
                      · {p.variants.length}
                    </span>
                  </h2>
                </CardHeader>
                <CardBody>
                  {p.variantAttributes.length > 0 ? (
                    <div className="mb-3 flex flex-col gap-2 border-b border-line pb-3">
                      {p.variantAttributes.map((a) => (
                        <div key={a.name} className="flex flex-wrap items-center gap-1.5">
                          <span className="min-w-16 text-sm text-muted">{a.name}</span>
                          {a.values.map((v) => (
                            <StatusPill key={v} tone="muted" size="sm">
                              {v}
                            </StatusPill>
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {p.variants.length === 0 ? (
                    <p className="text-sm text-muted">
                      No variants generated from these attributes yet.
                    </p>
                  ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] text-left text-sm">
                      <thead className="border-b border-line text-xs uppercase tracking-wide text-muted">
                        <tr>
                          <th className="px-2 py-2 font-medium">SKU</th>
                          <th className="px-2 py-2 font-medium">Combination</th>
                          <th className="px-2 py-2 font-medium">Barcode</th>
                          <th className="px-2 py-2 text-right font-medium">Weight (g)</th>
                          <th className="px-2 py-2 text-right font-medium">Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.variants.map((v) => (
                          <tr
                            key={Object.entries(v.attributeValues)
                              .map(([k, val]) => `${k}=${val}`)
                              .join("|")}
                            className="border-b border-line last:border-0"
                          >
                            <td className="px-2 py-2 font-mono text-xs">{v.sku ?? "—"}</td>
                            <td className="px-2 py-2 text-ink-2">
                              {Object.values(v.attributeValues).join(" · ")}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs">{v.barcode ?? "—"}</td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums">
                              {v.weightGrams ?? "—"}
                            </td>
                            {/* Derived from the bundles this variant is in
                                (ADR-0061) — `p.pricingEntries` is on this same
                                payload. `??` not `||`: a real 0 isn't blank. */}
                            <td className="px-2 py-2 text-right font-mono tabular-nums">
                              {variantQty.get(variantKey(v.attributeValues)) ?? "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                </CardBody>
              </Card>
            ) : null}
          </div>
        </StorePageColumn>
      )}

      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        title="Archive product?"
        description={`“${p?.name ?? ""}” will be pulled off the store. You can republish it later.`}
        confirmLabel="Archive"
        pending={vis.isPending}
        onConfirm={async () => {
          await vis.setVisibility(listingId, "archived");
          setArchiveOpen(false);
        }}
      />
    </StoreAdminLayout>
  );
}
